import { useEffect, useRef } from 'react';

const TRAIL_LENGTH = 80;
const COMET_SPEED = 7;
const PAUSE_BETWEEN = 2200; // ms between comets

function randomComet(W, H) {
  // Start from top or left edge, angle diagonally
  const fromLeft = Math.random() > 0.5;
  const x = fromLeft ? 0 : Math.random() * W * 0.6;
  const y = fromLeft ? Math.random() * H * 0.4 : 0;
  const angle = (Math.PI / 6) + Math.random() * (Math.PI / 8); // ~30-52deg diagonal
  return {
    x, y,
    vx: Math.cos(angle) * COMET_SPEED,
    vy: Math.sin(angle) * COMET_SPEED,
    trail: [],
  };
}

export function CometCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let comet = null;
    let waiting = false;

    function resize() {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function spawnComet() {
      comet = randomComet(canvas.width, canvas.height);
      waiting = false;
    }

    // Start first comet after a short delay
    setTimeout(spawnComet, 800);

    function draw() {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      if (comet) {
        // Add current position to trail
        comet.trail.push({ x: comet.x, y: comet.y });
        if (comet.trail.length > TRAIL_LENGTH) {
          comet.trail.shift();
        }

        // Draw trail
        for (let i = 0; i < comet.trail.length; i++) {
          const t = i / comet.trail.length;
          const alpha = t * 0.7;
          const width = t * 2.5;
          if (i === 0) continue;
          ctx.beginPath();
          ctx.moveTo(comet.trail[i - 1].x, comet.trail[i - 1].y);
          ctx.lineTo(comet.trail[i].x, comet.trail[i].y);
          ctx.strokeStyle = `rgba(255, 220, 120, ${alpha})`;
          ctx.lineWidth = width;
          ctx.lineCap = 'round';
          ctx.stroke();
        }

        // Draw comet head — bright glow
        const grd = ctx.createRadialGradient(comet.x, comet.y, 0, comet.x, comet.y, 6);
        grd.addColorStop(0, 'rgba(255, 255, 220, 1)');
        grd.addColorStop(0.4, 'rgba(255, 210, 80, 0.8)');
        grd.addColorStop(1, 'rgba(255, 180, 50, 0)');
        ctx.beginPath();
        ctx.arc(comet.x, comet.y, 6, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Move comet
        comet.x += comet.vx;
        comet.y += comet.vy;

        // Off screen — schedule next
        if (comet.x > W + TRAIL_LENGTH || comet.y > H + TRAIL_LENGTH) {
          comet = null;
          if (!waiting) {
            waiting = true;
            setTimeout(spawnComet, PAUSE_BETWEEN);
          }
        }
      }

      animId = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0, left: 0,
        width: '100%',
        height: '100%',
        display: 'block',
      }}
    />
  );
}
